import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Navbar: React.FC = () => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const handleMobileClose = () => setIsMobileMenuOpen(false);
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);


  const handleDropdownToggle = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  };

  const languages = [
    { code: "tr", label: "Türkçe", flag: "https://flagcdn.com/w40/tr.png" },
    { code: "en", label: "English", flag: "https://flagcdn.com/w40/gb.png" },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="relative flex items-center md:px-6 md:py-4 z-40 bg-gray-900 sm:px-6 py-4">
      {/* Logo */}
      <Link to="/">
        <div className="flex items-center cursor-pointer md:mt-0">
          <img src="/images/logo.png" alt="Logo" className="w-12 h-10 mr-2" />
          <div className="grid grid-cols-1 mt-1">
            <h1 className="text-lg text-white font-righteous">Ayvalık Marin</h1>
            <h2 className="text-xs text-cyan-300 opacity-40 font-montserrat -mt-1">
              {t("slogan")}
            </h2>
          </div>
        </div>
      </Link>

      {/* Masaüstü Menü */}
      <nav className="hidden xl:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 gap-12 text-sm font-semibold text-white font-montserrat">
        <Link to="/" className="hover:text-green-600">
          {t("home")}
        </Link>

        {/* Ürünler Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-green-400 w-auto"
            onClick={handleDropdownToggle}
          >
            {t("products")}
            <svg
              className={`w-4 h-4 transition-transform ${
                isDropdownOpen ? "rotate-180" : "rotate-0"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>

          {isDropdownOpen && (
            <div className="absolute -left-2 mt-2 w-64 bg-gray-900 border border-gray-200 rounded-md shadow-lg py-2 z-50 whitespace-nowrap">
              <Link
                to="/urunler/tekne-ve-yatlar"
                className="block px-4 py-2 hover:bg-green-500 hover:text-white"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t("boats")}
              </Link>
              <Link
                to="/urunler/ikinciel"
                className="block px-4 py-2 hover:bg-green-500 hover:text-white"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t("second")}
              </Link>
              <Link
                to="/urunler/aksesuarlar"
                className="block px-4 py-2 hover:bg-green-500 hover:text-white"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t("accessories")}
              </Link>
              <Link
                to="/urunler/motorlar"
                className="block px-4 py-2 hover:bg-green-500 hover:text-white"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t("engines")}
              </Link>
              <Link
                to="/urunler/sanalcapa"
                className="block px-4 py-2 hover:bg-green-500 hover:text-white"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t("anchor")}
              </Link>
            </div>
          )}
        </div>

        <Link to="/galeri" className="hover:text-green-400">
          {t("gallery")}
        </Link>
        <Link to="/ayvalik-marin" className="hover:text-green-400">
          {t("about")}
        </Link>
        <Link to="/haberler" className="hover:text-green-400">
          {t("news")}
        </Link>
        <Link to="/iletisim" className="hover:text-green-400">
          {t("contact")}
        </Link>
      </nav>

      {/* Sağ - Dil Seçici + Hamburger */}
      <div className="ml-auto flex items-center gap-6 text-white relative mr-2 mt-2">
        {/* Hamburger Icon */}
        <button
          onClick={() => {
            setIsMobileMenuOpen(!isMobileMenuOpen)}}
          className="xl:hidden focus:outline-none z-50"
        >
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Masaüstü - Dil Dropdown */}
        <div className="relative hidden xl:block -ml-60">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-2 bg-gray-800 px-2 py-1 rounded text-sm"
          >
            <img
              src={languages.find((l) => l.code === i18n.language)?.flag}
              alt="flag"
              className="w-7 h-5 rounded-sm"
            />
            <span>
              {languages.find((l) => l.code === i18n.language)?.label}
            </span>
            <svg
              className={`w-7 h-5 transition-transform ${
                langOpen ? "rotate-180" : "rotate-0"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {langOpen && (
            <div className="absolute right-0 mt-2 w-36 bg-gray-900 border border-gray-700 rounded shadow-md z-50">
              {languages.map((lang) => (
                <div
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className="flex items-center gap-2 px-3 py-2 text-white hover:bg-green-600 cursor-pointer"
                >
                  <img
                    src={lang.flag}
                    alt={lang.label}
                    className="w-5 h-4 rounded-sm"
                  />
                  <span className="text-sm">{lang.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobil Menü */}
      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-gray-900 border-t border-gray-800 text-white text-sm font-montserrat xl:hidden z-50">
          <div className="flex flex-col p-4 gap-2">
            <Link
              to="/"
              onClick={handleMobileClose}
              className="hover:text-green-400"
            >
              {t("home")}
            </Link>
            <details className="group">
              <summary className="cursor-pointer hover:text-green-400">
                {t("products")}
              </summary>
              <div className="flex flex-col pl-4 mt-2 text-sm gap-1">
                <Link
                  to="/urunler/tekne-ve-yatlar"
                  onClick={handleMobileClose}
                  className="hover:text-green-300"
                >
                  {t("boats")}
                </Link>

                <Link
                  to="/urunler/ikinciel"
                  onClick={handleMobileClose}
                  className="hover:text-green-300"
                >
                  {t("second")}
                </Link>

                <Link
                  to="/urunler/aksesuarlar"
                  onClick={handleMobileClose}
                  className="hover:text-green-300"
                >
                  {t("accessories")}
                </Link>
                <Link
                  to="/urunler/motorlar"
                  onClick={handleMobileClose}
                  className="hover:text-green-300"
                >
                  {t("engines")}
                </Link>
                <Link
                  to="/urunler/sanalcapa"
                  onClick={handleMobileClose}
                  className="hover:text-green-300"
                >
                  {t("anchor")}
                </Link>
              </div>
            </details>
            <Link
              to="/galeri"
              onClick={handleMobileClose}
              className="hover:text-green-400"
            >
              {t("gallery")}
            </Link>
            <Link
              to="/ayvalik-marin"
              onClick={handleMobileClose}
              className="hover:text-green-400"
            >
              {t("about")}
            </Link>
            <Link
              to="/haberler"
              onClick={handleMobileClose}
              className="hover:text-green-400"
            >
              {t("news")}
            </Link>
            <Link
              to="/iletisim"
              onClick={handleMobileClose}
              className="hover:text-green-400"
            >
              {t("contact")}
            </Link>

            {/* Mobil Dil Seçici */}
            <div className="flex mt-4 gap-2 items-center">
              <img
                src={languages.find((l) => l.code === i18n.language)?.flag}
                alt="flag"
                className="w-6 h-4 rounded-sm"
              />
              <select
                onChange={(e) => changeLanguage(e.target.value)}
                value={i18n.language}
                className="bg-gray-800 text-white px-2 py-1 rounded text-sm"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
